import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on, Action } from '@ngrx/store'

import * as TeamsActions from './teams.actions'
import { TeamsEntity } from './teams.models'
import { GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'

export const TEAMS_FEATURE_KEY = 'teams'

export interface TeamsState extends EntityState<TeamsEntity> {
  selectedId?: string | number
  loaded: boolean
  error?: string | null
  count: number
  dataView: GridSwitchEnum
  searchHistory: string[]
  editCreateLoaded: boolean
}

export interface TeamsPartialState {
  readonly [TEAMS_FEATURE_KEY]: TeamsState
}

export const teamsAdapter: EntityAdapter<TeamsEntity> = createEntityAdapter<TeamsEntity>({
  selectId: (team: TeamsEntity) => team.uuid,
})

export const initialTeamsState: TeamsState = teamsAdapter.getInitialState({
  loaded: true,
  count: 0,
  dataView: GridSwitchEnum.Table,
  searchHistory: [],
  editCreateLoaded: true,
})

const reducer = createReducer(
  initialTeamsState,
  on(TeamsActions.loadTeams, (state, action) =>
    action.shouldReset
      ? teamsAdapter.removeAll({ ...state, loaded: false, selectedId: undefined })
      : { ...state, loaded: false },
  ),
  on(TeamsActions.loadTeamsSuccess, (state, action) =>
    teamsAdapter.upsertMany(action.teams, {
      ...state,
      count: action.count,
      loaded: true,
      editCreateLoaded: true,
    }),
  ),
  on(TeamsActions.loadTeamsFailure, (state) => ({ ...state, loaded: true })),
  on(TeamsActions.updateDataView, (state, { dataView }) => ({
    ...state,
    dataView: dataView,
  })),
  on(
    TeamsActions.createTeam,
    TeamsActions.updateTeam,
    TeamsActions.assignProfiles,
    TeamsActions.removeProfiles,
    (state) => ({
      ...state,
      editCreateLoaded: false,
    }),
  ),
  on(
    TeamsActions.createTeamSuccess,
    TeamsActions.createTeamFailure,
    TeamsActions.updateTeamFailure,
    TeamsActions.assignProfilesSuccess,
    TeamsActions.assignProfilesFailure,
    TeamsActions.removeProfilesSuccess,
    TeamsActions.removeProfilesFailure,
    (state) => ({
      ...state,
      editCreateLoaded: true,
    }),
  ),
  on(TeamsActions.updateTeamSuccess, (state, { team }) =>
    teamsAdapter.updateOne(team, { ...state, editCreateLoaded: true }),
  ),
  on(TeamsActions.updateTeamForRealtime, (state, action) => teamsAdapter.updateOne(action.update, state)),
  on(TeamsActions.deleteTeamSuccess, (state, { team }) => teamsAdapter.removeOne(team.uuid, state)),
  on(TeamsActions.clearSelectedTeam, (state) => ({
    ...state,
    selectedId: undefined,
  })),
  on(TeamsActions.openTeamProfileDialog, (state, { uuid }) => ({ ...state, selectedId: uuid })),
)

export function teamsReducer(state: TeamsState | undefined, action: Action) {
  return reducer(state, action)
}
